import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, FileText, X } from 'lucide-react';
import StudentDetailPanel from './StudentDetailPanel';

// Update the service to use the correct student endpoints and include JWT token
const studentService = {
  getStudents: async (tutorId) => {
    const token = localStorage.getItem('auth_token'); // Retrieve the token from localStorage
    const response = await fetch(`${process.env.REACT_APP_API_URL}/students/me`, {
      headers: {
        'Authorization': `Bearer ${token}` // Attach the token in the Authorization header
      }
    });
    return response.json();
  },

  addStudent: async (studentData) => {
    const token = localStorage.getItem('auth_token'); // Retrieve the token from localStorage
    const response = await fetch(`${process.env.REACT_APP_API_URL}/students/me`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` // Attach the token in the Authorization header
      },
      body: JSON.stringify(studentData)
    });
    if (!response.ok) {
      const data = await response.json();
console.log(data);  
      throw new Error('Failed to add student');
    }
    return studentData;
  },

  deleteStudent: async (studentId) => {
    const token = localStorage.getItem('auth_token'); // Retrieve the token from localStorage
    const response = await fetch(`${process.env.REACT_APP_API_URL}/students/me/${studentId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}` // Attach the token in the Authorization header
      }
    });
    if (!response.ok) {
      throw new Error('Failed to delete student');
    }
    return true;
  }
};

const Student = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    notes: [],
    password: '',
  });
  const [isEditingStudent, setIsEditingStudent] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
        // Parse user data and get tutorId from the tutor object
        const tutorId = localStorage.getItem('tutor_id');

        if (!tutorId) throw new Error("Tutor ID is missing");

        const data = await studentService.getStudents(tutorId);

        setStudents(data);
        setLoading(false);
        setNewStudent({
            ...newStudent,    // Preserve existing properties in newStudent
            tutor: { id: tutorId }, // Add or update the tutor field with the tutorId
            notes: [],
        });
    } catch (error) {
        console.error('Error fetching students:', error);
        setLoading(false);
    }
  };

  const handleAddStudent = async () => {
    try {
      await studentService.addStudent(newStudent);
      // Refresh the student list
      fetchStudents();
      // Reset the form and close the modal
      setIsAddingStudent(false);
      setNewStudent({ firstName: '', lastName: '', email: '', notes: [], password: '' });
    } catch (error) {
      console.error('Error adding student:', error);
      alert('Failed to add student. Please try again.');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await studentService.deleteStudent(studentId);
        // Update the local state to remove the deleted student
        setStudents(students.filter(student => student.id !== studentId));
      } catch (error) {
        console.error('Error deleting student:', error);
        alert('Failed to delete student. Please try again.');
      }
    }
  };

  const handleEditClick = (student) => {
    setSelectedStudent(student);
    setIsEditingStudent(true);
  };

  const handleViewNotes = (student) => {
    setSelectedStudent(student);
    setIsEditingStudent(true);
    // We'll pass a flag to StudentDetailPanel to open directly to Notes tab
    setSelectedStudent({...student, openNotesTab: true});
  };

  const handleCloseEdit = () => {
    setIsEditingStudent(false);
    setSelectedStudent(null);
  };

  const filteredStudents = students.filter(student => 
    (student.firstName + ' ' + student.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-4 sm:p-6 bg-gray-50">
      {/* Header with search and add button */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search students..."
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm transition-all duration-200"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          onClick={() => setIsAddingStudent(true)}
          className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Student
        </button>
      </div>

      {/* Student list - Desktop Table / Mobile Cards */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex-grow">
        {filteredStudents.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">Notes</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredStudents.map((student, index) => (
                    <tr key={student.id} className={`hover:bg-blue-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{student.firstName} {student.lastName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-600">{student.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button 
                          className="inline-flex items-center px-3 py-1.5 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-150 font-medium"
                          onClick={() => handleViewNotes(student)}
                        >
                          <FileText className="w-4 h-4 mr-1.5" />
                          View Notes
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <button 
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-150"
                            onClick={() => handleEditClick(student)}
                            title="Edit student"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-150"
                            onClick={() => handleDeleteStudent(student.id)}
                            title="Delete student"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden p-4 space-y-4">
              {filteredStudents.map((student) => (
                <div key={student.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{student.firstName} {student.lastName}</h3>
                      <p className="text-gray-600 text-sm">{student.email}</p>
                    </div>
                    <div className="flex">
                      <button 
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-150"
                        onClick={() => handleEditClick(student)}
                        title="Edit student"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-150"
                        onClick={() => handleDeleteStudent(student.id)}
                        title="Delete student"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <button 
                    className="w-full flex items-center justify-center px-4 py-2 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-150 font-medium"
                    onClick={() => handleViewNotes(student)}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Notes
                  </button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16 px-4">
            <div className="max-w-sm mx-auto">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No students found' : 'No students yet'}
              </h3>
              <p className="text-gray-500">
                {searchTerm ? 'Try adjusting your search terms' : 'Add your first student to get started'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add student modal */}
      {isAddingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <div className='gap-2 flex flex-col'>
                <h2 className="text-2xl font-bold text-gray-900">Add New Student</h2>
                <p className='text-sm font-semibold text-blue-400'>New students receive a confirmation email giving access to all lesson plans.</p>
              </div>
              <button
                onClick={() => setIsAddingStudent(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-150"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  value={newStudent.firstName}
                  onChange={(e) => setNewStudent({...newStudent, firstName: e.target.value})}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  value={newStudent.lastName}
                  onChange={(e) => setNewStudent({...newStudent, lastName: e.target.value})}
                  placeholder="Enter last name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  value={newStudent.email}
                  onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Student Password</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  value={newStudent.password}
                  onChange={(e) => setNewStudent({...newStudent, password: e.target.value})}
                  placeholder="Enter a secure password"
                />
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setIsAddingStudent(false)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddStudent}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
              >
                Add Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit student modal using StudentDetailPanel */}
      {isEditingStudent && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Student Details</h2>
              <button
                onClick={handleCloseEdit}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-150"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <StudentDetailPanel 
                student={selectedStudent} 
                onClose={handleCloseEdit}
                onSave={() => {
                  handleCloseEdit();
                  fetchStudents(); // Refresh the list after saving
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Student;